import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, MONTHS_ES, formatMoney, formatPct } from "../lib/api";
import { ArrowLeft, ArrowRight, Plus, Minus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Compare() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [versions, setVersions] = useState([]);
  const [v1, setV1] = useState(null);
  const [v2, setV2] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.listVersions(planId).then((d) => {
      setPlan(d.plan);
      setVersions(d.versions);
      if (d.versions.length >= 1) {
        const current = d.plan.current_version_number;
        setV2(current);
        setV1(current > 1 ? current - 1 : current);
      }
    });
  }, [planId]);

  useEffect(() => {
    if (v1 && v2) {
      api.compareVersions(planId, v1, v2).then(setData).catch(() => toast.error("Error al comparar"));
    }
  }, [v1, v2, planId]);

  if (!plan) return <div className="p-6">Cargando…</div>;

  return (
    <div>
      <div className="border-b border-border bg-card px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/plans/${planId}`)} data-testid="back-btn" className="p-2 rounded-sm hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="label-xs text-muted-foreground">
              {MONTHS_ES[plan.month]} · {plan.year} · {plan.name}
            </div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">Comparar Versiones</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={v1 ? String(v1) : ""} onValueChange={(v) => setV1(parseInt(v))}>
            <SelectTrigger data-testid="v1-selector" className="w-32 rounded-sm">
              <SelectValue placeholder="Base" />
            </SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={v.version_number} value={String(v.version_number)}>V{v.version_number}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ArrowRight size={16} className="text-muted-foreground" />
          <Select value={v2 ? String(v2) : ""} onValueChange={(v) => setV2(parseInt(v))}>
            <SelectTrigger data-testid="v2-selector" className="w-32 rounded-sm">
              <SelectValue placeholder="Comparar" />
            </SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={v.version_number} value={String(v.version_number)}>V{v.version_number}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data ? (
        <div className="p-6 text-muted-foreground">Selecciona dos versiones para comparar.</div>
      ) : (
        <div className="p-6 space-y-6">
          {/* Balance diff */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DiffCard label="Saldo inicial" diff={data.diff.balance_diff.initial_balance} />
            <DiffCard label="Total ingresos" diff={data.diff.balance_diff.total_income} />
            <DiffCard label="Total egresos" diff={data.diff.balance_diff.total_expense} invert />
            <DiffCard label="Saldo final" diff={data.diff.balance_diff.final_balance} highlight />
          </div>

          {/* Summary chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <Chip color="emerald" icon={Plus} label={`${data.diff.added.length} agregadas`} testid="added-count" />
            <Chip color="amber" icon={PencilSimple} label={`${data.diff.modified.length} modificadas`} testid="modified-count" />
            <Chip color="red" icon={Minus} label={`${data.diff.removed.length} eliminadas`} testid="removed-count" />
            <Chip color="slate" label={`${data.diff.unchanged.length} sin cambios`} testid="unchanged-count" />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="diff" className="w-full">
            <TabsList className="rounded-sm" data-testid="compare-tabs">
              <TabsTrigger value="diff" data-testid="tab-diff" className="rounded-sm">Vista Diff</TabsTrigger>
              <TabsTrigger value="side" data-testid="tab-side" className="rounded-sm">Lado a Lado</TabsTrigger>
            </TabsList>

            <TabsContent value="diff" className="mt-4">
              <DiffTable diff={data.diff} />
            </TabsContent>

            <TabsContent value="side" className="mt-4">
              <SideBySide v1={data.v1} v2={data.v2} v1num={v1} v2num={v2} />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

function DiffCard({ label, diff, highlight, invert }) {
  const delta = diff.delta || 0;
  // For expense, an increase (positive delta) is negative for cashflow
  const effectiveNeg = invert ? delta > 0 : delta < 0;
  const cls = delta === 0 ? "text-muted-foreground" : effectiveNeg ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400";
  return (
    <div className={`card-elevated p-5 ${highlight ? "border-2 border-foreground" : ""}`} data-testid={`diff-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="label-xs text-muted-foreground">{label}</div>
      <div className="mt-3 space-y-1 text-sm">
        <div className="flex items-center justify-between"><span className="text-muted-foreground">Antes</span><span className="font-mono-tabular">{formatMoney(diff.old)}</span></div>
        <div className="flex items-center justify-between"><span className="text-muted-foreground">Después</span><span className="font-mono-tabular font-semibold">{formatMoney(diff.new)}</span></div>
      </div>
      <div className="mt-3 pt-3 border-t border-border flex items-end justify-between">
        <div>
          <div className="label-xs text-muted-foreground">Δ</div>
          <div className={`font-mono-tabular font-bold text-lg ${cls}`}>
            {delta >= 0 ? "+" : ""}{formatMoney(delta)}
          </div>
        </div>
        <div className={`font-mono-tabular text-sm ${cls}`}>{formatPct(diff.pct)}</div>
      </div>
    </div>
  );
}

function Chip({ color, icon: Icon, label, testid }) {
  const colorMap = {
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
    red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
    slate: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm label-xs ${colorMap[color]}`}>
      {Icon && <Icon size={12} weight="bold" />}
      {label}
    </span>
  );
}

function DiffTable({ diff }) {
  const rows = [
    ...diff.added.map((e) => ({ ...e, _kind: "added" })),
    ...diff.modified.map((m) => ({ ...m.new, _kind: "modified", _changes: m.changes, _old: m.old })),
    ...diff.removed.map((e) => ({ ...e, _kind: "removed" })),
  ];
  if (rows.length === 0) {
    return <div className="card-elevated p-10 text-center text-muted-foreground">No hay diferencias entre estas versiones.</div>;
  }
  return (
    <div className="card-elevated overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className="text-left px-3 py-2 label-xs text-muted-foreground w-28">Cambio</th>
            <th className="text-left px-3 py-2 label-xs text-muted-foreground">Partida</th>
            <th className="text-left px-3 py-2 label-xs text-muted-foreground">Categoría</th>
            <th className="text-right px-3 py-2 label-xs text-muted-foreground">Monto</th>
            <th className="text-left px-3 py-2 label-xs text-muted-foreground">Fecha</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className={`border-b border-border ${r._kind === "added" ? "diff-added" : r._kind === "removed" ? "diff-removed" : "diff-modified"}`} data-testid={`diff-row-${r._kind}-${idx}`}>
              <td className="px-3 py-3">
                <span className="label-xs">
                  {r._kind === "added" && "+ Agregado"}
                  {r._kind === "removed" && "− Eliminado"}
                  {r._kind === "modified" && "~ Modificado"}
                </span>
              </td>
              <td className="px-3 py-3">
                <div className="font-medium">{r.description}</div>
                {r._kind === "modified" && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {Object.entries(r._changes).map(([f, v]) => (
                      <div key={f}><span className="font-semibold">{f}:</span> {String(v.old)} → <span className="font-semibold">{String(v.new)}</span></div>
                    ))}
                  </div>
                )}
              </td>
              <td className="px-3 py-3">
                <div>{r.category}</div>
                {r.subcategory && <div className="text-xs text-muted-foreground">{r.subcategory}</div>}
              </td>
              <td className={`px-3 py-3 text-right font-mono-tabular font-semibold ${r.type === "income" ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                {r.type === "expense" && "-"}{formatMoney(Number(r.amount))}
              </td>
              <td className="px-3 py-3 font-mono-tabular text-xs">{r.expected_date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SideBySide({ v1, v2, v1num, v2num }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <VersionPanel label={`V${v1num} (Base)`} meta={v1.meta} totals={v1.totals} />
      <VersionPanel label={`V${v2num}`} meta={v2.meta} totals={v2.totals} highlight />
    </div>
  );
}

function VersionPanel({ label, meta, totals, highlight }) {
  return (
    <div className={`card-elevated ${highlight ? "border-foreground" : ""}`}>
      <div className="p-5 border-b border-border">
        <div className="label-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-sm">{meta.change_description}</div>
        <div className="text-xs text-muted-foreground mt-2">
          {meta.author} · {new Date(meta.created_at).toLocaleString("es-ES")}
        </div>
      </div>
      <div className="p-5 border-b border-border grid grid-cols-2 gap-3">
        <div><div className="label-xs text-muted-foreground">Saldo inicial</div><div className="font-mono-tabular font-semibold mt-0.5">{formatMoney(meta.initial_balance)}</div></div>
        <div><div className="label-xs text-muted-foreground">Ingresos</div><div className="font-mono-tabular font-semibold mt-0.5 text-emerald-600 dark:text-emerald-400">{formatMoney(totals.total_income)}</div></div>
        <div><div className="label-xs text-muted-foreground">Egresos</div><div className="font-mono-tabular font-semibold mt-0.5 text-red-600 dark:text-red-400">{formatMoney(totals.total_expense)}</div></div>
        <div><div className="label-xs text-muted-foreground">Saldo final</div><div className={`font-mono-tabular font-black text-lg mt-0.5 ${totals.final_balance >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{formatMoney(totals.final_balance)}</div></div>
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-3 py-2 label-xs text-muted-foreground">Tipo</th>
              <th className="text-left px-3 py-2 label-xs text-muted-foreground">Descripción</th>
              <th className="text-right px-3 py-2 label-xs text-muted-foreground">Monto</th>
            </tr>
          </thead>
          <tbody>
            {meta.entries.length === 0 && (
              <tr><td colSpan={3} className="text-center text-muted-foreground py-8">Sin partidas</td></tr>
            )}
            {meta.entries.map((e) => (
              <tr key={e.id} className="border-b border-border">
                <td className="px-3 py-2">
                  <span className={`label-xs px-1.5 py-0.5 rounded-sm ${e.type === "income" ? "income-chip" : "expense-chip"}`}>
                    {e.type === "income" ? "In" : "Eg"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="text-sm">{e.description}</div>
                  <div className="text-xs text-muted-foreground">{e.category}{e.subcategory ? ` · ${e.subcategory}` : ""}</div>
                </td>
                <td className={`px-3 py-2 text-right font-mono-tabular font-semibold ${e.type === "income" ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                  {e.type === "expense" && "-"}{formatMoney(Number(e.amount))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
